import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  PageHeader,
  PageHeaderTop,
  PageHeaderTitle,
  PageHeaderHeading,
  PageHeaderDescription,
  PageHeaderActions,
  DataTable,
  DataTablePagination,
  DataTableToolbar,
  DataTableToolbarSpacer,
  DataTableScroll,
  DataTableEmpty,
  DataTableSkeleton,
  DataTableFooter,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  StatusPill,
  Button,
  Badge,
  Checkbox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  StatCard,
  cn,
  type StatusPillStatus,
} from "@eduviaos/ui-kit";
import {
  ClipboardList,
  Download,
  GraduationCap,
  KanbanSquare,
  ListFilter,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Search,
  TrendingUp,
  UserPlus2,
  X,
  XOctagon,
} from "lucide-react";
import type {
  AdmissionEnquiry,
  AdmissionFunnelStage,
  EnquiryListFilters,
  EnquiryListResponse,
} from "@eduviaos/shared";
import { useEnquiries, useFunnel } from "@/hooks/useAdmissions";
import { useAuth } from "@/auth/AuthProvider";
import { useEnabledModules } from "@/hooks/useEnabledModules";
import { getVerticalProfile } from "@/lib/verticalProfile";
import { usePageSizePreference } from "@/hooks/usePageSizePreference";
import { api } from "@/lib/api";
import { ExportCsvErrorBanner } from "@/components/ExportCsvErrorBanner";
import { useCsvExport } from "@/hooks/useCsvExport";
import { ADMISSIONS_CSV_COLUMNS, mapEnquiryToCsvRow } from "@/lib/admissionsCsv";
import { EnquiryCreateSheet } from "@/components/EnquiryCreateSheet";
import { EnquiryRejectDialog } from "@/components/EnquiryRejectDialog";
import { EnquiryDeleteDialog } from "@/components/EnquiryDeleteDialog";
import { AdmissionsBoard } from "@/components/AdmissionsBoard";
import { BulkActionBar } from "@/components/BulkActionBar";
import { AdmissionsSubNav } from "@/components/AdmissionsSubNav";
import { ApplyPortalStatusChip } from "@/components/ApplyPortalStatusChip";

/**
 * Admissions list — Phase D1b
 * ---------------------------------------------------------------
 * Operational hub for the IA's admissions funnel. Replaces the
 * row-click-opens-sheet UX with a proper list + detail-route +
 * board-view pattern.
 *
 * Surfaces every D1a BE addition the IA needs:
 *   - q-search across student/parent name + contacts (debounced 280ms)
 *   - source filter (capability-driven dropdown)
 *   - stage filter (funnel-driven; falls back to "all" if no funnel
 *     config — should never happen in production but safe to render)
 *   - server-side pagination (50/page default, kbd-navigable footer)
 *   - row "…" actions (Open, Reject, Move to bin) — Iron Rule #16
 *
 * View toggle:
 *   - Table — DataTable per Iron Rule #16; click row → /admissions/:id
 *   - Board — funnel-aware kanban columns; click card → /admissions/:id
 *
 * Hero KPIs use the in-memory enquiry slice + funnel counts so they
 * update instantly when the user drags a filter — no extra round-trip.
 */

// Stage → StatusPill variant. The funnel config exposes color hints
// per stage but the StatusPill primitive only ships 5 variants — so we
// map by `terminal_outcome` (canonical) and fall back to a neutral
// "invited" for in-flight stages.
function stagePillVariant(stage: AdmissionFunnelStage | undefined): StatusPillStatus {
  if (!stage) return "invited";
  if (stage.terminal_outcome === "admitted") return "active";
  if (stage.terminal_outcome === "rejected") return "archived";
  return "invited";
}

// Phase E1.f — default rows-per-page; the user can override via the
// shared `<DataTablePagination>` selector (persisted per-user via
// localStorage scope "admissions"). The URL still owns `?page=` so
// deep-links stay shareable, but pageSize is a per-user preference
// and intentionally not URL-state — different recipients of a shared
// link should see the link's data through THEIR preferred density.
const DEFAULT_PAGE_SIZE = 10;

const SOURCE_FILTER_OPTIONS = [
  { value: "", label: "All sources" },
  { value: "walk_in", label: "Walk-in" },
  { value: "online_form", label: "Online form" },
  { value: "referral", label: "Referral" },
  { value: "phone", label: "Phone enquiry" },
  { value: "ad_campaign", label: "Ad campaign" },
  { value: "other", label: "Other" },
];

type ViewMode = "table" | "board";

export function AdmissionsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const tenantSlug = user?.tenant_id ?? null;

  // SC Foundation (F0d) — de-school the operational admissions surface
  // per the tenant vertical. A study centre runs walk-in enquiries for
  // "Learners" and has no parents/guardians, so we relabel the person
  // noun and drop parent-shaped copy + the parent column. School /
  // coaching keep their exact "Student" + "Parent" vocabulary. The
  // enquiry data model (`student_name` / `parent_name`) is BE-owned and
  // shared, so we only change what the IA *reads*, never field names.
  const { vertical } = useEnabledModules();
  const profile = getVerticalProfile(vertical ?? "school");
  const personOne = profile.personNoun.one;
  const showParent = profile.has.parents;

  // ── URL-as-source-of-truth for filter state ─────────────────────────────
  // The URL carries q/source/stage/page so the IA can deep-link a
  // filter to a colleague ("hey check the walk-ins from this week").
  // Local state only mirrors the q-input for debouncing.
  const urlQ = searchParams.get("q") ?? "";
  const urlSource = searchParams.get("source") ?? "";
  const urlStage = searchParams.get("stage") ?? "";
  const urlPage = Math.max(1, Number(searchParams.get("page") ?? "1") || 1);
  const urlView = (searchParams.get("view") as ViewMode | null) ?? "table";
  const [pageSize, setPageSize] = usePageSizePreference({
    scope: "admissions",
    defaultPageSize: DEFAULT_PAGE_SIZE,
  });

  const [qInput, setQInput] = React.useState(urlQ);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [rowAction, setRowAction] = React.useState<
    | { kind: "reject"; enquiry: AdmissionEnquiry }
    | { kind: "delete"; enquiry: AdmissionEnquiry }
    | null
  >(null);
  // Phase D4 — multi-row selection. Stored as a Set keyed by enquiry id
  // so toggling stays O(1) and we never serialise selection to the URL
  // (selection is a transient thing — closing the tab clears it).
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(() => new Set());

  // Selection survives within the same page of results but resets on
  // any filter / page change to avoid the "I just deleted 200 invisible
  // rows" footgun.
  React.useEffect(() => {
    setSelectedIds(new Set());
  }, [urlQ, urlSource, urlStage, urlPage]);

  // Keep input in sync with URL (e.g. after a reset).
  React.useEffect(() => {
    setQInput(urlQ);
  }, [urlQ]);

  // Debounce the q-input → URL to avoid hammering the BE on every
  // keystroke. 280ms is the IDE-search-feel sweet spot — fast enough
  // to feel live, slow enough to dedupe a typist's bursts.
  React.useEffect(() => {
    if (qInput === urlQ) return;
    const t = window.setTimeout(() => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (qInput.trim()) {
            next.set("q", qInput.trim());
          } else {
            next.delete("q");
          }
          next.delete("page");
          return next;
        },
        { replace: true },
      );
    }, 280);
    return () => window.clearTimeout(t);
  }, [qInput, urlQ, setSearchParams]);

  const setSearchParam = React.useCallback(
    (key: string, value: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) {
            next.set(key, value);
          } else {
            next.delete(key);
          }
          if (key !== "page") next.delete("page");
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const filters: EnquiryListFilters = React.useMemo(
    () => ({
      q: urlQ || undefined,
      source: urlSource || undefined,
      stage_key: urlStage || undefined,
      limit: pageSize,
      offset: (urlPage - 1) * pageSize,
    }),
    [urlQ, urlSource, urlStage, urlPage, pageSize],
  );

  const { data, isLoading, isFetching, refetch, isError } = useEnquiries(filters);
  const { data: funnel, isLoading: funnelLoading } = useFunnel();

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  // ── Selection helpers (Phase D4) ────────────────────────────────────────
  const allOnPageSelected = items.length > 0 && items.every((e) => selectedIds.has(e.id));
  const someOnPageSelected = !allOnPageSelected && items.some((e) => selectedIds.has(e.id));
  const togglePageSelection = (checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        for (const e of items) next.add(e.id);
      } else {
        for (const e of items) next.delete(e.id);
      }
      return next;
    });
  };
  const toggleRowSelection = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };
  const clearSelection = React.useCallback(() => setSelectedIds(new Set()), []);
  const dropSucceededIds = React.useCallback((ids: string[]) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.delete(id);
      return next;
    });
  }, []);
  // `<DataTablePagination>` derives totalPages internally; the page
  // only needs the "X–Y of Z" footer string below.
  const start = total === 0 ? 0 : (urlPage - 1) * pageSize + 1;
  const end = Math.min(urlPage * pageSize, total);

  // Build a stage_key → AdmissionFunnelStage lookup so the table can
  // render labels + tone without re-resolving on every cell.
  const stageMap = React.useMemo(() => {
    const m = new Map<string, AdmissionFunnelStage>();
    if (funnel) for (const s of funnel.stages) m.set(s.stage_key, s);
    return m;
  }, [funnel]);

  // KPIs derived from the funnel response — accurate even when the
  // current page is paginated. (Funnel returns full counts per stage
  // regardless of list pagination, so total active = sum of
  // non-terminal stages.)
  const kpis = React.useMemo(() => {
    if (!funnel) return null;
    const live = funnel.stages.filter((s) => !s.is_terminal);
    const admitted = funnel.stages.find((s) => s.terminal_outcome === "admitted");
    const rejected = funnel.stages.find((s) => s.terminal_outcome === "rejected");
    const active = live.reduce((sum, s) => sum + s.count, 0);
    const admittedCount = admitted?.count ?? 0;
    const rejectedCount = rejected?.count ?? 0;
    const decided = admittedCount + rejectedCount;
    const conversionRate = decided > 0 ? Math.round((admittedCount / decided) * 100) : null;
    return { active, admittedCount, rejectedCount, conversionRate };
  }, [funnel]);

  const hasActiveFilter = Boolean(urlQ || urlSource || urlStage);

  // ── Phase D2.a — CSV export ──────────────────────────────────────────────
  // Iterates `GET /admissions/enquiries` in 200-row chunks honouring the
  // active `q` / `source` / `stage` filters (server-side; same shape as
  // the visible toolbar selection), assembles an RFC 4180-compliant CSV
  // via the shared `apps/institution-admin/src/lib/csv.ts` primitive,
  // and triggers a client-side download. Output filename:
  // `admissions-<tenant_slug>-<YYYY-MM-DD>.csv`. Errors surface through
  // a dismissable inline banner above the existing list error.
  // Phase D2.f — CSV export wiring delegated to the shared
  // `useCsvExport` primitive. See StudentsTab for the migration
  // pattern.
  const csvExport = useCsvExport({
    filenamePrefix: "admissions",
    tenantSlug,
    disabled: total === 0 && !isLoading,
    formatError: (err) =>
      err instanceof Error && err.message
        ? `Couldn't export the admissions list as CSV: ${err.message}`
        : "Couldn't export the admissions list as CSV.",
    buildRows: async () => {
      const collected: AdmissionEnquiry[] = [];
      const CHUNK = 200;
      const HARD_CAP = 50_000;
      let offset = 0;
      let expectedTotal: number | null = null;
      while (collected.length < HARD_CAP) {
        const search = new URLSearchParams();
        if (urlQ) search.set("q", urlQ);
        if (urlSource) search.set("source", urlSource);
        if (urlStage) search.set("stage_key", urlStage);
        search.set("limit", String(CHUNK));
        search.set("offset", String(offset));
        const page = await api.get<EnquiryListResponse>(
          `/admissions/enquiries?${search.toString()}`,
        );
        collected.push(...page.items);
        if (expectedTotal === null) expectedTotal = page.total;
        offset += page.items.length;
        if (page.items.length === 0) break;
        if (page.items.length < CHUNK) break;
        if (expectedTotal !== null && collected.length >= expectedTotal) break;
      }

      const stageLookup = new Map<string, AdmissionFunnelStage>();
      if (funnel) for (const s of funnel.stages) stageLookup.set(s.stage_key, s);

      return {
        columns: ADMISSIONS_CSV_COLUMNS,
        rows: collected.map((enquiry) => mapEnquiryToCsvRow(enquiry, stageLookup)),
      };
    },
  });

  const exportDisabled = csvExport.isExporting || (total === 0 && !isLoading);

  const clearFilters = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("q");
        next.delete("source");
        next.delete("stage");
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  };

  return (
    <>
      <AdmissionsSubNav />
      <PageHeader>
        <PageHeaderTop>
          <PageHeaderTitle>
            <PageHeaderHeading>Admissions</PageHeaderHeading>
            <PageHeaderDescription>
              Every prospective {personOne.toLowerCase()} in your funnel — search, filter, drill in.
            </PageHeaderDescription>
            {/* Phase H2 — apply portal lifecycle status chip. Click
                to open the lifecycle settings page. */}
            <div className="mt-2">
              <ApplyPortalStatusChip />
            </div>
          </PageHeaderTitle>
          <PageHeaderActions>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
              aria-label="Refresh enquiries"
            >
              <RefreshCw
                className={cn("size-4", isFetching && "animate-spin")}
                aria-hidden="true"
              />
              Refresh
            </Button>
            <Button
              size="sm"
              onClick={() => setCreateOpen(true)}
              data-testid="enquiry-create-trigger"
            >
              <Plus className="size-4" aria-hidden="true" />
              New enquiry
            </Button>
          </PageHeaderActions>
        </PageHeaderTop>
      </PageHeader>

      {/* ── KPI strip (funnel-driven) ───────────────────────────────────── */}
      {kpis && (
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            icon={<UserPlus2 className="size-4" aria-hidden="true" />}
            label="Active enquiries"
            value={kpis.active.toLocaleString()}
            caption="Across all live funnel stages"
            tone="blue"
          />
          <StatCard
            icon={<GraduationCap className="size-4" aria-hidden="true" />}
            label="Admitted"
            value={kpis.admittedCount.toLocaleString()}
            caption="Converted to admission records"
            tone="green"
          />
          <StatCard
            icon={<XOctagon className="size-4" aria-hidden="true" />}
            label="Rejected"
            value={kpis.rejectedCount.toLocaleString()}
            caption="Closed funnel — searchable in audit"
            tone="amber"
          />
          <StatCard
            icon={<TrendingUp className="size-4" aria-hidden="true" />}
            label="Conversion rate"
            value={kpis.conversionRate === null ? "—" : `${kpis.conversionRate.toLocaleString()}%`}
            caption={
              kpis.conversionRate === null
                ? "Need at least one terminal outcome"
                : "Admitted / (admitted + rejected)"
            }
            tone={kpis.conversionRate === null ? "neutral" : "violet"}
          />
        </div>
      )}

      {/* ── View toggle + content ───────────────────────────────────────── */}
      <Tabs
        value={urlView}
        onValueChange={(v) => setSearchParam("view", v === "table" ? null : v)}
        className="mt-6"
      >
        <div className="flex items-center justify-between gap-3">
          <TabsList>
            <TabsTrigger value="table" data-testid="view-table">
              <ListFilter className="mr-1.5 size-3.5" aria-hidden="true" />
              List
            </TabsTrigger>
            <TabsTrigger value="board" data-testid="view-board">
              <KanbanSquare className="mr-1.5 size-3.5" aria-hidden="true" />
              Board
            </TabsTrigger>
          </TabsList>
          <span className="text-muted-foreground text-xs tabular-nums">
            {isLoading
              ? "Loading…"
              : `${total.toLocaleString()} enquir${total === 1 ? "y" : "ies"}`}
          </span>
        </div>

        <TabsContent value="table" className="mt-4 space-y-2">
          <ExportCsvErrorBanner
            variant="inline"
            testid="admissions-export-error"
            error={csvExport.exportError}
            onDismiss={csvExport.dismissError}
          />
          <BulkActionBar
            selectedIds={Array.from(selectedIds)}
            onClear={clearSelection}
            onSucceededIds={dropSucceededIds}
            stages={funnel?.stages ?? []}
            currentFilters={filters}
          />
          <DataTable responsive="cards">
            <DataTableToolbar>
              <div className="relative w-full sm:w-72">
                <Search
                  className="text-muted-foreground absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2"
                  aria-hidden="true"
                />
                <Input
                  type="search"
                  value={qInput}
                  onChange={(e) => setQInput(e.target.value)}
                  placeholder={
                    showParent ? "Search name, parent, email, phone…" : "Search name, email, phone…"
                  }
                  className="pl-8"
                  aria-label="Search enquiries"
                  data-testid="enquiries-search"
                />
              </div>

              <Select
                value={urlSource || "__all__"}
                onValueChange={(v) => setSearchParam("source", v === "__all__" ? null : v)}
              >
                <SelectTrigger
                  className="h-9 w-[160px]"
                  aria-label="Filter by source"
                  data-testid="filter-source"
                >
                  <SelectValue placeholder="All sources" />
                </SelectTrigger>
                <SelectContent>
                  {SOURCE_FILTER_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value || "__all__"} value={opt.value || "__all__"}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={urlStage || "__all__"}
                onValueChange={(v) => setSearchParam("stage", v === "__all__" ? null : v)}
                disabled={funnelLoading || !funnel}
              >
                <SelectTrigger
                  className="h-9 w-[180px]"
                  aria-label="Filter by stage"
                  data-testid="filter-stage"
                >
                  <SelectValue placeholder="All stages" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All stages</SelectItem>
                  {funnel?.stages
                    .slice()
                    .sort((a, b) => a.order - b.order)
                    .map((s) => (
                      <SelectItem key={s.stage_key} value={s.stage_key}>
                        {s.label}
                        <span className="text-muted-foreground ml-2 text-xs tabular-nums">
                          ({s.count})
                        </span>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>

              {hasActiveFilter && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  data-testid="filters-clear"
                >
                  <X className="size-3.5" aria-hidden="true" />
                  Clear
                </Button>
              )}

              <DataTableToolbarSpacer />

              <Button
                variant="ghost"
                size="sm"
                onClick={csvExport.handleExport}
                disabled={exportDisabled}
                aria-label="Download admissions list as CSV"
                data-testid="admissions-export-csv"
              >
                <Download
                  aria-hidden="true"
                  className={cn("size-4", csvExport.isExporting && "animate-pulse")}
                />
                {csvExport.isExporting ? "Exporting…" : "Download CSV"}
              </Button>
            </DataTableToolbar>

            <DataTableScroll>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px]">
                      <Checkbox
                        aria-label={
                          allOnPageSelected
                            ? "Clear selection on this page"
                            : "Select every enquiry on this page"
                        }
                        checked={
                          allOnPageSelected ? true : someOnPageSelected ? "indeterminate" : false
                        }
                        onCheckedChange={(c) => togglePageSelection(c === true)}
                        data-testid="select-all-page"
                      />
                    </TableHead>
                    <TableHead className="w-[24%]">{personOne}</TableHead>
                    <TableHead className="w-[22%]">Contact</TableHead>
                    <TableHead className="w-[16%]">Stage</TableHead>
                    <TableHead className="w-[10%]">Source</TableHead>
                    <TableHead className="w-[14%] text-right">Last advanced</TableHead>
                    <TableHead className="w-[8%] text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                {isLoading ? (
                  <DataTableSkeleton rows={6} columns={7} />
                ) : (
                  <TableBody>
                    {items.map((e) => {
                      const stage = stageMap.get(e.stage_key);
                      const checked = selectedIds.has(e.id);
                      return (
                        <TableRow
                          key={e.id}
                          className={cn(
                            "hover:bg-muted/40 cursor-pointer",
                            checked && "bg-primary/5",
                          )}
                          onClick={() => navigate(`/admissions/${e.id}`)}
                          data-testid={`enquiry-row-${e.id}`}
                          data-state={checked ? "selected" : undefined}
                        >
                          <TableCell onClick={(ev) => ev.stopPropagation()}>
                            <Checkbox
                              aria-label={`Select enquiry for ${e.student_name}`}
                              checked={checked}
                              onCheckedChange={(c) => toggleRowSelection(e.id, c === true)}
                              data-testid={`enquiry-select-${e.id}`}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="text-foreground font-medium">{e.student_name}</div>
                            {showParent && e.parent_name && (
                              <div className="text-muted-foreground text-xs">
                                Parent: {e.parent_name}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="text-foreground text-sm">{e.student_email ?? "—"}</div>
                            <div className="text-muted-foreground text-xs">
                              {e.student_phone ?? "—"}
                            </div>
                          </TableCell>
                          <TableCell>
                            <StatusPill status={stagePillVariant(stage)}>
                              {stage?.label ?? e.stage_key}
                            </StatusPill>
                          </TableCell>
                          <TableCell>
                            {e.source ? (
                              <Badge variant="outline" className="font-normal">
                                {e.source.replace(/_/g, " ")}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground text-xs">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-muted-foreground text-right text-xs tabular-nums">
                            {e.last_advanced_at
                              ? new Date(e.last_advanced_at).toLocaleDateString(undefined, {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })
                              : "—"}
                          </TableCell>
                          <TableCell className="text-right" onClick={(ev) => ev.stopPropagation()}>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  aria-label={`Actions for ${e.student_name}`}
                                  data-testid={`enquiry-actions-${e.id}`}
                                >
                                  <MoreHorizontal className="size-4" aria-hidden="true" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="min-w-[160px]">
                                <DropdownMenuItem onSelect={() => navigate(`/admissions/${e.id}`)}>
                                  Open detail
                                </DropdownMenuItem>
                                {stage?.terminal_outcome !== "rejected" && (
                                  <DropdownMenuItem
                                    onSelect={() => setRowAction({ kind: "reject", enquiry: e })}
                                    className="text-destructive focus:text-destructive"
                                  >
                                    Reject…
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onSelect={() => setRowAction({ kind: "delete", enquiry: e })}
                                  className="text-destructive focus:text-destructive"
                                >
                                  Move to bin…
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                )}
              </Table>
            </DataTableScroll>

            {!isLoading && items.length === 0 && (
              <DataTableEmpty
                icon={<ClipboardList aria-hidden="true" />}
                title={
                  isError
                    ? "Couldn't load enquiries"
                    : hasActiveFilter
                      ? "No matches"
                      : "No enquiries yet"
                }
                description={
                  isError
                    ? "Try refreshing — if it keeps happening, check your network."
                    : hasActiveFilter
                      ? "Try adjusting your search or clearing the filters."
                      : `Click 'New enquiry' to add the first prospective ${personOne.toLowerCase()} to your funnel.`
                }
                action={
                  isError ? (
                    <Button variant="outline" size="sm" onClick={() => refetch()}>
                      <RefreshCw className="size-4" aria-hidden="true" />
                      Retry
                    </Button>
                  ) : hasActiveFilter ? (
                    <Button variant="outline" size="sm" onClick={clearFilters}>
                      <X className="size-3.5" aria-hidden="true" />
                      Clear filters
                    </Button>
                  ) : (
                    <Button size="sm" onClick={() => setCreateOpen(true)}>
                      <Plus className="size-4" aria-hidden="true" />
                      New enquiry
                    </Button>
                  )
                }
              />
            )}

            {!isLoading && total > 0 && (
              <DataTableFooter>
                <span className="text-muted-foreground text-sm tabular-nums">
                  Showing {start.toLocaleString()}–{end.toLocaleString()} of{" "}
                  {total.toLocaleString()}
                </span>
                <DataTablePagination
                  page={urlPage}
                  pageSize={pageSize}
                  totalRows={total}
                  isFetching={isFetching}
                  onPageChange={(next) => setSearchParam("page", next === 1 ? null : String(next))}
                  onPageSizeChange={(next) => {
                    setPageSize(next);
                    // Reset to page 1 — otherwise switching from 50 → 10 on
                    // page 2 lands the user mid-empty-slice. Drop ?page= so
                    // the URL stays clean.
                    setSearchParam("page", null);
                  }}
                  testIdPrefix="admissions-pagination"
                />
              </DataTableFooter>
            )}
          </DataTable>
        </TabsContent>

        <TabsContent value="board" className="mt-4">
          {funnelLoading || !funnel ? (
            <div className="border-border/60 bg-card flex min-h-[480px] items-center justify-center rounded-lg border">
              <span className="text-muted-foreground text-sm">Loading funnel…</span>
            </div>
          ) : (
            <AdmissionsBoard funnel={funnel} enquiries={items} />
          )}
        </TabsContent>
      </Tabs>

      <EnquiryCreateSheet
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(id) => navigate(`/admissions/${id}`)}
      />

      {rowAction?.kind === "reject" && (
        <EnquiryRejectDialog
          open
          onOpenChange={(o) => !o && setRowAction(null)}
          enquiryId={rowAction.enquiry.id}
          studentName={rowAction.enquiry.student_name}
        />
      )}
      {rowAction?.kind === "delete" && (
        <EnquiryDeleteDialog
          open
          onOpenChange={(o) => !o && setRowAction(null)}
          enquiryId={rowAction.enquiry.id}
          studentName={rowAction.enquiry.student_name}
        />
      )}
    </>
  );
}
